import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';

const VALUES = [
  {
    title: 'Built for practitioners',
    desc: 'PitchFriendly is built by people who\'ve run PR campaigns and know what a real workflow looks like — not by engineers guessing at it.',
  },
  {
    title: 'Transparency first',
    desc: 'Simple pricing, honest features, no dark patterns. We\'d rather earn your trust with a great product than lock you in with a contract.',
  },
  {
    title: 'Respect your time',
    desc: 'Every feature is designed to save you hours, not add to your workload. If something takes more than a few clicks, we rethink it.',
  },
  {
    title: 'Relationships over volume',
    desc: 'We believe the best PR is built on genuine journalist relationships. Our tools help you be more relevant, not just louder.',
  },
];

export default function AboutPage() {
  useEffect(() => {
    document.title = 'About — PitchFriendly';
  }, []);

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="bg-gray-950 text-white py-20 text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">
            We built the PR tool<br />we always wanted
          </h1>
          <p className="text-gray-300 text-lg leading-relaxed">
            PitchFriendly started as a frustration. Too many spreadsheets, too many browser tabs, too many journalists who never responded because the pitch wasn't relevant. We built something better.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our mission</h2>
              <p className="text-gray-500 leading-relaxed mb-4">
                PR is one of the most relationship-driven fields in business — but the tools most agencies use are either overbuilt enterprise platforms or cobbled-together Google Sheets.
              </p>
              <p className="text-gray-500 leading-relaxed mb-4">
                PitchFriendly's mission is to give every PR professional — from solo freelancers to 50-person agencies — access to tools that make their work faster, more targeted, and more effective.
              </p>
              <p className="text-gray-500 leading-relaxed">
                We think great PR comes from finding the right journalist at the right time with the right story. We're building the infrastructure that makes that possible.
              </p>
            </div>
            <div className="bg-blue-50 rounded-2xl p-8">
              <blockquote className="text-lg text-blue-900 italic leading-relaxed">
                "The best pitch is one that feels like it was written just for the journalist reading it — because it was."
              </blockquote>
              <p className="mt-4 text-sm text-blue-700 font-medium">— PitchFriendly founding principle</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">What we believe</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {VALUES.map((v) => (
              <div key={v.title} className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-3">{v.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">A small team, seriously focused</h2>
          <p className="text-gray-500 leading-relaxed max-w-2xl mx-auto mb-12">
            We're a small, focused team shipping fast and listening closely to the PR professionals who use PitchFriendly every day. Every piece of feedback shapes the product.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8">
            {[
              { initials: 'PD', name: 'Paul Denya', role: 'Founder & Product' },
            ].map((member) => (
              <div key={member.name} className="text-center">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg mx-auto mb-3">
                  {member.initials}
                </div>
                <p className="font-semibold text-gray-900">{member.name}</p>
                <p className="text-sm text-gray-500">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-50 border-t border-gray-100 text-center">
        <div className="max-w-xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Want to talk to us?</h2>
          <p className="text-gray-500 mb-6">We'd love to hear from you — whether it's a product question, partnership idea, or just to say hi.</p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/contact" className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors">
              Get in touch
            </Link>
            <Link to="/register" className="px-6 py-3 bg-white border border-gray-200 text-gray-900 rounded-xl font-semibold hover:bg-gray-50 transition-colors">
              Try PitchFriendly free
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
